/**
 * Desk API client (B1, issue #422, epic #421).
 *
 * Typed fetchers over the read-only `/api/desk/*` surface — the canonical
 * membership read model the Membership operating desk (F1, issue #426) is
 * built on: `GET /api/desk/summary`, `GET /api/desk/pipeline` and
 * `GET /api/desk/people?bucket=`/`?stage=`. No trailing slash — matches the
 * backend routes (`backend/apps/desk/urls.py`).
 */
import { apiRequest } from "./client";
import type {
  DeskPeopleResponse,
  DeskPipeline,
  DeskSummary,
  DeskGraceWindowKey,
  DeskReviewFilter,
  DoctorLinkFilter,
  StudentStagnationQueue,
} from "./desk.types";

/** `GET /api/desk/summary` — the 8 bucket counts + KPI tiles. */
export function getDeskSummary(): Promise<DeskSummary> {
  return apiRequest<DeskSummary>("/desk/summary");
}

/** `GET /api/desk/pipeline` — the renewal pipeline stages. */
export function getDeskPipeline(): Promise<DeskPipeline> {
  return apiRequest<DeskPipeline>("/desk/pipeline");
}

/** Backend page-size defaults (`apps.desk.selectors`). */
export const DESK_PEOPLE_DEFAULT_LIMIT = 25;
export const DESK_PEOPLE_MAX_LIMIT = 200;

export interface DeskPeopleQuery {
  bucket?: string;
  stage?: string;
  /**
   * Issue #563: the "Last 12 months" trend block's joined/lapsed bar
   * drill-down — a THIRD selection mode alongside `bucket`/`stage` (never
   * more than one, mirrors `apps.desk.serializers.PeopleQuerySerializer`).
   * `"YYYY-MM"`, the exact same key `getLifecycleMovement`'s series carries.
   * Always paired with `event`.
   */
  month?: string;
  /** Issue #563: which of `month`'s two ledger transitions to list — required together with `month`. */
  event?: "joined" | "lapsed";
  limit?: number;
  offset?: number;
  /** Free-text search (issue #493, S3) — narrows WITHIN the given bucket/stage/month. */
  q?: string;
  /** Issue #543: only valid together with `bucket: "ca"` — 400 otherwise. */
  doctor_link?: DoctorLinkFilter;
  /**
   * Issue #599 (#664 added `"suspended"`): only valid together with a
   * reviewable queue — `stage: "payment_issues"` / `"grace"` /
   * `"suspended"` (see `DESK_REVIEWABLE_STAGES`) — 400 otherwise.
   * `"needs_action"` (#734) is open and resurfaced together.
   */
  review_state?: DeskReviewFilter;
  /**
   * Issue #666: only valid together with `stage: "grace"` — 400 otherwise.
   * Narrows the one lapsed queue to one of its two display windows
   * (`DESK_GRACE_WINDOW_KEYS`); it never selects a different stage.
   */
  window?: DeskGraceWindowKey;
}

/**
 * `GET /api/desk/people?bucket=`/`?stage=`/`?month=&event=` — the paginated
 * drill-down list behind every count on the desk. Exactly one of
 * `bucket`/`stage`/`month` is valid (mirrors
 * `apps.desk.serializers.PeopleQuerySerializer`). `signal` (issue #493) lets
 * a caller abort an in-flight request — e.g. a debounced search box
 * cancelling a stale request when the query changes again.
 */
export function getDeskPeople(
  query: DeskPeopleQuery,
  signal?: AbortSignal,
): Promise<DeskPeopleResponse> {
  const params = new URLSearchParams();
  if (query.bucket) params.set("bucket", query.bucket);
  if (query.stage) params.set("stage", query.stage);
  if (query.month) params.set("month", query.month);
  if (query.event) params.set("event", query.event);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.offset !== undefined) params.set("offset", String(query.offset));
  if (query.q) params.set("q", query.q);
  if (query.doctor_link) params.set("doctor_link", query.doctor_link);
  if (query.review_state) params.set("review_state", query.review_state);
  if (query.window) params.set("window", query.window);
  const qs = params.toString();
  return apiRequest<DeskPeopleResponse>(`/desk/people${qs ? `?${qs}` : ""}`, { signal });
}

/**
 * `GET /api/desk/queues/student-stagnation` — the "Students past the
 * N-month mark" review queue (issue #503 Part 2). Read-only; acknowledging a
 * case is a WRITE and lives on the outreach API (see `api/outreach.ts`
 * `createCaseAcknowledgement`/`deleteCaseAcknowledgement`), not here.
 */
export function getStudentStagnationQueue(): Promise<StudentStagnationQueue> {
  return apiRequest<StudentStagnationQueue>("/desk/queues/student-stagnation");
}
